import { auth, db } from '../config/firebase'
import { doc, onSnapshot } from 'firebase/firestore'

const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * Get the current user's auth token
 */
async function getAuthToken() {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }
  return user.getIdToken()
}

/**
 * Get user's credit balance from API
 */
export async function getBalance() {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE}/api/credits/balance`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to fetch balance')
  }
  
  return response.json()
}

/**
 * Use credits for a service
 * @param {number} amount - Number of credits to use
 * @param {string} service - Service identifier (e.g., 'elevenlabs', 'n8n')
 * @param {string} description - Description of the usage
 */
export async function useCredits(amount, service, description) {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE}/api/credits/use`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ amount, service, description }),
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to use credits')
  }
  
  return response.json()
}

/**
 * Get credit usage history
 * @param {number} limit - Maximum number of records to fetch
 */
export async function getCreditHistory(limit = 50) {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE}/api/credits/history?limit=${limit}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to fetch history')
  }
  
  return response.json()
}

/**
 * Subscribe to real-time credit balance updates
 * @param {string} userId - The Firebase user ID
 * @param {function} callback - Callback with balance data
 * @returns {function} Unsubscribe function
 */
export function subscribeToBalance(userId, callback) {
  const monetizationRef = doc(db, 'users', userId, 'monetization', 'status')
  
  return onSnapshot(monetizationRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data()
      callback({
        creditsBalance: data.creditsBalance || 0,
        creditsAllottedMonthly: data.creditsAllottedMonthly || 0,
        creditsUsedThisMonth: data.creditsUsedThisMonth || 0,
        creditsPurchased: data.creditsPurchased || 0,
        plan: data.plan || 'free',
        lastCreditReset: data.lastCreditReset?.toDate() || null,
      })
    } else {
      callback({
        creditsBalance: 0,
        creditsAllottedMonthly: 0,
        creditsUsedThisMonth: 0,
        creditsPurchased: 0,
        plan: 'free',
        lastCreditReset: null,
      })
    }
  }, (error) => {
    console.error('Error subscribing to balance:', error)
    callback(null, error)
  })
}

/**
 * Check if user has sufficient credits
 * @param {number} required - Credits required
 */
export async function hasSufficientCredits(required) {
  const { creditsBalance } = await getBalance()
  return creditsBalance >= required
}

/**
 * Format credits for display
 * @param {number} credits - Credit amount
 */
export function formatCredits(credits) {
  return new Intl.NumberFormat('en-US').format(credits)
}
